import { useState, useEffect, useRef } from 'react';

interface SlideshowArtwork {
  id: number;
  imageUrl: string;
  title: string;
}

interface ArtworkSlideshowProps {
  artworks: SlideshowArtwork[];
  fallbackSrc: string;
}

const SLIDE_DURATION = 3000; // 3 seconds visible
const FADE_DURATION = 1200;  // 1.2 seconds crossfade

export default function ArtworkSlideshow({ artworks, fallbackSrc }: ArtworkSlideshowProps) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [nextIndex, setNextIndex] = useState(1);
  const [isFading, setIsFading] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const fadeRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const slides = artworks.length > 0 ? artworks : null;

  useEffect(() => {
    if (!slides || slides.length <= 1) return;

    const startCycle = () => {
      timerRef.current = setTimeout(() => {
        const next = (currentIndex + 1) % slides.length;
        setNextIndex(next);
        setIsFading(true);

        fadeRef.current = setTimeout(() => {
          setCurrentIndex(next);
          setIsFading(false);
          startCycle();
        }, FADE_DURATION);
      }, SLIDE_DURATION);
    };

    startCycle();

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      if (fadeRef.current) clearTimeout(fadeRef.current);
    };
  }, [currentIndex, slides]);

  if (!slides) {
    return (
      <div className="absolute inset-0">
        <img
          src={fallbackSrc}
          alt="Gallery background"
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-white/40" />
      </div>
    );
  }

  const currentSrc = slides[currentIndex]?.imageUrl;
  const nextSrc = slides[nextIndex]?.imageUrl;

  return (
    <div className="absolute inset-0 overflow-hidden">
      {/* Current slide */}
      <div
        className="absolute inset-0 transition-opacity"
        style={{
          opacity: isFading ? 0 : 1,
          transitionDuration: `${FADE_DURATION}ms`,
          transitionTimingFunction: 'ease-in-out',
        }}
      >
        <img
          src={currentSrc}
          alt="Artwork"
          className="w-full h-full object-cover object-top"
          draggable={false}
        />
      </div>

      {/* Next slide (fades in underneath / on top) */}
      <div
        className="absolute inset-0 transition-opacity"
        style={{
          opacity: isFading ? 1 : 0,
          transitionDuration: `${FADE_DURATION}ms`,
          transitionTimingFunction: 'ease-in-out',
        }}
      >
        <img
          src={nextSrc}
          alt="Artwork next"
          className="w-full h-full object-cover object-top"
          draggable={false}
        />
      </div>

      {/* Subtle dark overlay so text stays readable */}
      <div className="absolute inset-0 bg-black/35 pointer-events-none" />
    </div>
  );
}
